import { Panel } from '../../../layout/Panel';
import PieChartWithNeedle from '../../../layout/components/PieChartWithNeedle';

const dialData = [
    { name: 'Lagging', value: 20, color: '#ef4444' },
    { name: 'Watch', value: 20, color: '#f97316' },
    { name: 'Neutral', value: 20, color: '#facc15' },
    { name: 'Positive', value: 20, color: '#84cc16' },
    { name: 'Leader', value: 20, color: '#10b981' },
];

const dialValue = (score, min = -6, max = 6) => {
    const numericValue = Number(score ?? 0);
    return Math.max(0, Math.min(100, ((numericValue - min) / (max - min)) * 100));
};

const scoreText = (value) => {
    const numericValue = Number(value ?? 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (numericValue > 0) return "border-lime-200 bg-lime-50 text-lime-700";
    if (numericValue === 0) return "border-slate-200 bg-slate-50 text-slate-600";
    if (numericValue > -2) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-red-200 bg-red-50 text-red-700";
};

const scoreLabel = (value) => {
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "Leader";
    if (numericValue > 0) return "Positive";
    if (numericValue === 0) return "Neutral";
    if (numericValue > -2) return "Watch";
    return "Lagging";
};

const barWidth = (value) => {
    const numericValue = Number(value ?? 0);
    return `${Math.max(8, Math.min(100, ((numericValue + 2) / 4) * 100))}%`;
};

const EsgMetricCard = ({ metric }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{metric.category}</div>
                <h3 className="mt-1 text-lg font-bold text-slate-950">{metric.title}</h3>
            </div>
            <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(metric.value)}`}>
                {scoreText(metric.value)}
            </div>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-600">{metric.description}</p>
        <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                <span>Relative signal</span>
                <span>{scoreLabel(metric.value)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: barWidth(metric.value) }} />
            </div>
        </div>
    </div>
);

const PeerTile = ({ label, value }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-xl font-bold text-slate-950">{scoreText(value)}</div>
    </div>
);

export const EsgTab = ({ esgScore }) => {
    const envScore = Number(esgScore.environment || 0);
    const socialScore = Number(esgScore.social || 0);
    const governanceScore = Number(esgScore.governance || 0);
    const totalScore = envScore + socialScore + governanceScore;

    const metrics = [
        {
            category: "Environmental",
            title: "Environmental Score",
            value: envScore,
            description: "Measures environmental positioning including emissions and resource indicators where available.",
        },
        {
            category: "Social",
            title: "Social Score",
            value: socialScore,
            description: "Captures workforce, customer, and community-related sustainability indicators.",
        },
        {
            category: "Governance",
            title: "Governance Score",
            value: governanceScore,
            description: "Reflects board, control, shareholder, and governance quality signals.",
        },
    ];

    const hasPeerData = [
        esgScore.peer_esg_score_performance_min,
        esgScore.peer_esg_score_performance_avg,
        esgScore.peer_esg_score_performance_max,
    ].some((value) => value !== undefined && value !== null);

    return (
        <div className="space-y-4">
            <Panel className="border border-slate-200 shadow-sm">
                <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-center">
                    <div className="flex justify-center">
                        <PieChartWithNeedle data={dialData} width={200} height={160} value={dialValue(totalScore)} />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">ESG view</p>
                        <h2 className="mt-2 text-2xl font-bold text-slate-950">ESG Score {scoreText(totalScore)}</h2>
                        <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                            Environmental, social, and governance signals are normalized onto the same comparable scale used across the platform.
                        </p>
                    </div>
                    <div className={`w-fit rounded-full border px-4 py-2 text-sm font-bold ${scoreTone(totalScore)}`}>
                        {scoreLabel(totalScore)}
                    </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-3">
                    <PeerTile label="Peer Low" value={esgScore.peer_esg_score_performance_min || 0} />
                    <PeerTile label="Peer Avg" value={esgScore.peer_esg_score_performance_avg || 0} />
                    <PeerTile label="Peer High" value={esgScore.peer_esg_score_performance_max || 0} />
                </div>
                {!hasPeerData ? (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                        Peer ESG benchmarks are not available for this company yet, so the visible score is based on available company data.
                    </div>
                ) : null}
            </Panel>

            <div className="grid gap-4 xl:grid-cols-3">
                {metrics.map((metric) => (
                    <EsgMetricCard key={metric.title} metric={metric} />
                ))}
            </div>
        </div>
    );
};
